import $ from 'jquery';
import EventEmitter from '../core/event-emitter';
import inherit from '../core/inherit';
import VideoPlaylist from '../core/video-playlist';
import template from '../templates/player.hbs';
import { OPEN, NAVIGATE, READY, DESTROY, LOAD, SWITCH } from "./events";
import { YOUTUBE, KAKAOTV } from "../constants";
import YoutubePlayer from '../core/youtube-player';
import KakaoTVPlayer from '../core/kakaotv-url-player';

const $template = $.templates(template);

function VideoPlayer({ autoplayYoutube, autoplayKakao, playerWrapperId, playlistSelector }) {
  EventEmitter.call(this);

  // initial state setting 
  this.$player          = null;
  this.history          = [];
  this.autoplayYoutube  = autoplayYoutube;
  this.autoplayKakao    = autoplayKakao;
  this.playerWrapperId  = playerWrapperId;
  this.playlistSelector = playlistSelector;
  this.isPlaying        = false;

  // core module instance
  this._playlist      = VideoPlaylist.create();
  this._youtubePlayer = YoutubePlayer.create(playerWrapperId);
  this._kakaoTVPlayer = KakaoTVPlayer.create({ autoplay: this.autoplayKakao });

  // player event binding
  this._bindYoutubePlayerEvents();
  this._bindKakaoTVPlayerEvents();
  this._bindPlayListEvents();
}

inherit(VideoPlayer, EventEmitter, {
  _bindPlayListEvents() {
    const pl    = this._playlist;
    const eType = VideoPlaylist.events;
    pl.on(eType.ITEM_CLICK, (e) => {
      const videoId    = $(e.currentTarget).data('vid');
      const clipData   = pl.templateData.documents.filter(data => data.videoId === videoId)[0];
      const nowPlaying = this.nowPlaying();

      // 플레이어를 바로 닫으면 이벤트 위임할 수 없게 되므로 타임아웃으로 비동기 처리한다
      setTimeout(() => {
        // 현재 플레이어의 객체에 새롭게 재생할 데이터를 오버라이딩한다.
        // API 콜을 하면 모든 데이터를 내려주지 않기 때문에 필요한 데이터만 덮어 쓴다.
        this.close().open($.extend({}, nowPlaying, clipData), true);
        this.emit(SWITCH, $.extend(e, { detail: { clipData, nowPlaying } }));
      }, 0);

    });
    pl.on(eType.NAVIGATE, (e) => {
      this.emit(NAVIGATE, e);
    });
  },
  _bindYoutubePlayerEvents() {
    const yp    = this._youtubePlayer;
    const eType = YoutubePlayer.events;
    yp.on(eType.READY, (e) => {
      this.autoplayYoutube && e.target.playVideo();
      this.emit(READY, { event: e, type: YOUTUBE });
    });
    yp.on(eType.LOAD, (videoId) => {
      yp.play(videoId);
    });
    yp.on(eType.DESTROY, () => {
      this.emit(DESTROY);
    });
  },
  _bindKakaoTVPlayerEvents() {
    const kp    = this._kakaoTVPlayer;
    const eType = KakaoTVPlayer.events;
    kp.on(eType.LOAD, () => {
      this.emit(LOAD);
    });
    kp.on(eType.READY, (e) => {
      this.emit(READY, { event: e, type: KAKAOTV });
    });
    kp.on(eType.DESTROY, () => {
      this.emit(DESTROY);
    });
  },
  /**
   * 플레이어 DOM 영역이 그려져있다면 삭제한다.
   * @private
   */
  _destroy() {
    this.$player && this.$player.remove();
  },
  /**
   * 템플릿을 그린 뒤 jQuery 인스턴스로 파싱한다.
   * @param {PlayerTemplateData} clip
   * @private
   */
  _renderPlayer(clip) {
    const templateData = $.extend({}, clip, { playerId: this.playerWrapperId });
    this.$player       = $($template.render(templateData));
  },
  nowPlaying() {
    return this.history.slice(-1).pop();
  },
  /**
   * 현재 생성된 iFrame DOM 엘리먼트 또는 Div를 잡아온다. js로 그렸다 지우는 DOM이기 때문에 매번 잡아온다.
   * @returns { jQuery }
   */
  getPlayer$() {
    return this.$player.find(`#${this.playerWrapperId}`);
  },

  /**
   * 플레이어를 로드한다.
   * @param {Object} clip
   * @param {string} clip.cpType
   * @param {string} clip.title
   */
  loadPlayer({ cpType, videoId, title }) {
    if (cpType === YOUTUBE) {
      this._youtubePlayer.load(videoId);
    } else {
      this._kakaoTVPlayer.load(this.getPlayer$(), { videoId, title });
    }
  },
  /**
   *
   * @param clip
   * @returns {VideoPlayer}
   */
  open(clip) {
    this._renderPlayer(clip);
    this.loadPlayer(clip);
    this.history.push(clip);
    this.isPlaying = true;
    this.emit(OPEN, clip);
    return this;
  },

  /**
   * 플레이어, 플레이리스트, 플레이어 컨테이너를 차례대로 dom에서 지운다.
   * 동적으로 바인딩한 이벤트 리스너를 모두 지우기때문에 플레이어를 없애려면 반드시 이 메소드를 사용하여야한다.
   * @returns {VideoPlayer}
   */
  close() {
    this.isPlaying = false;
    this._youtubePlayer.destroy();
    this._kakaoTVPlayer.destroy();
    this._playlist.destroy();
    this._destroy();
    return this;
  },

  /**
   * 체이닝을 위해 한번 감싸서 오버라이딩
   * @override
   * @param args
   * @returns {VideoPlayer}
   */
  on(...args) {
    EventEmitter.prototype.on.apply(this, args);
    return this;
  },

  /**
   * 재생목록을 그린다.
   * @param {PlayListTemplateData} templateData
   */
  renderPlayList(templateData) {
    this.$player.find(this.playlistSelector).replaceWith(this._playlist.render(templateData));
  },

  /**
   * 하단 동영상 목록 영역을 없앤다.
   * 플레이리스트를 불러오는 AJAX통신이 실패했을 경우 빈 영역이 그대로 남아있게 되므로, 해당영역을 지워줄 필요가 있다.
   */
  closeFooter() {
    if (this.$player) {
      this.$player.find(this.playlistSelector).remove();
    }
  }
});

export default VideoPlayer;


/** WEBPACK FOOTER **
 ** ./src/VO2/libs/video-player/video-player.js
 **/